import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertCircle, FileText, Lock, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { citationLabel, citationStyle, confidenceClass, confidenceLabel, sourceCategory } from "../lib/citations";

function Badge({ children }) {
    return (
        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded bg-slate-50 text-slate-600 border-slate-200">
            {children}
        </span>
    );
}

export default function SharedAppealReview() {
    const { token } = useParams();
    const [review, setReview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        setLoading(true);
        api.get(`/public/appeal-shares/${token}`)
            .then((res) => {
                setReview(res.data);
                setError("");
            })
            .catch((err) => {
                setError(err?.response?.data?.detail || "Review link could not be opened");
                setReview(null);
            })
            .finally(() => setLoading(false));
    }, [token]);

    if (loading) {
        return (
            <main className="min-h-screen bg-slate-50 p-6">
                <div className="max-w-4xl mx-auto text-sm text-slate-600">Loading review link...</div>
            </main>
        );
    }

    if (error) {
        return (
            <main className="min-h-screen bg-slate-50 p-6">
                <section className="max-w-3xl mx-auto border border-red-200 bg-white rounded-lg p-6">
                    <div className="flex items-center gap-2 text-red-700 font-semibold">
                        <AlertCircle size={18} /> Review link unavailable
                    </div>
                    <p className="text-sm text-slate-600 mt-2">{error}</p>
                </section>
            </main>
        );
    }

    const draft = review.draft || {};
    const intake = draft.intake || {};
    const analysis = draft.analysis || {};
    const selectedIds = new Set(draft.selected_ground_ids || []);
    const selectedGrounds = (analysis.grounds || []).filter((g) => selectedIds.has(g.id));

    return (
        <main className="min-h-screen bg-slate-50 p-4 md:p-8" data-testid="shared-appeal-page">
            <div className="max-w-5xl mx-auto space-y-5">
                <section className="border border-slate-200 bg-white rounded-lg p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                            <div className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
                                <ShieldCheck size={13} /> Read-only professional review
                            </div>
                            <h1 className="font-heading text-2xl font-bold text-slate-900 mt-1">PCN appeal draft</h1>
                            <p className="text-sm text-slate-600 mt-2">
                                Shared by {review.owner_name}. This page does not provide estate portal access or private document downloads.
                            </p>
                        </div>
                        <div className="text-right text-xs text-slate-500">
                            <div>Expires</div>
                            <div className="font-semibold text-slate-800">{review.expires_at}</div>
                        </div>
                    </div>
                    {review.note && (
                        <div className="mt-4 border border-slate-200 bg-slate-50 rounded-md p-3 text-sm text-slate-700">
                            {review.note}
                        </div>
                    )}
                </section>

                <section className="border border-slate-200 bg-white rounded-lg p-5">
                    <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">PCN details</div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                        <div><span className="text-slate-500">Reference:</span> <span className="font-semibold text-slate-800">{intake.pcn_reference}</span></div>
                        <div><span className="text-slate-500">Issue date:</span> <span className="font-semibold text-slate-800">{intake.issue_date}</span></div>
                        <div><span className="text-slate-500">Vehicle:</span> <span className="font-semibold text-slate-800">{intake.vehicle_reg}</span></div>
                        <div><span className="text-slate-500">Issuer:</span> <span className="font-semibold text-slate-800">{intake.issuing_authority}</span></div>
                        <div className="md:col-span-2"><span className="text-slate-500">Location:</span> <span className="font-semibold text-slate-800">{intake.location}</span></div>
                        <div className="md:col-span-2"><span className="text-slate-500">Allegation:</span> <span className="font-semibold text-slate-800">{intake.alleged_contravention}</span></div>
                    </div>
                </section>

                {selectedGrounds.length > 0 && (
                    <section className="border border-slate-200 bg-white rounded-lg p-5">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Selected appeal grounds</div>
                        <div className="space-y-3">
                            {selectedGrounds.map((ground) => (
                                <div key={ground.id} className="border border-slate-200 rounded-md p-3">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge>{ground.strength || "ground"}</Badge>
                                        <h2 className="font-heading text-sm font-semibold text-slate-900">{ground.title}</h2>
                                    </div>
                                    <p className="text-sm text-slate-700 mt-1">{ground.description}</p>
                                    {ground.rationale && <p className="text-xs text-slate-500 mt-1">Why this applies: {ground.rationale}</p>}
                                </div>
                            ))}
                        </div>
                    </section>
                )}

                <section className="border border-slate-200 bg-white rounded-lg p-5">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
                        <FileText size={13} /> Draft appeal letter
                    </div>
                    <pre className="whitespace-pre-wrap text-sm text-slate-800 font-body">{draft.appeal_letter}</pre>
                </section>

                {draft.citations?.length > 0 && (
                    <section className="border border-slate-200 bg-white rounded-lg p-5">
                        <div className="text-xs uppercase tracking-widest text-slate-500 mb-3">Citations</div>
                        <ul className="space-y-3">
                            {draft.citations.map((citation, index) => (
                                <li key={index} className="text-sm text-slate-700">
                                    <span
                                        className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded"
                                        style={{
                                            backgroundColor: citationStyle(citation).bg,
                                            color: citationStyle(citation).text,
                                            borderColor: citationStyle(citation).border,
                                        }}
                                    >
                                        {citationLabel(citation)}
                                    </span>
                                    <span className={`ml-2 inline-flex items-center px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide border rounded ${confidenceClass(citation)}`}>
                                        {confidenceLabel(citation)}
                                    </span>
                                    <span className="ml-2 text-xs text-slate-500">{sourceCategory(citation)}</span>
                                    {citation.document_title && <strong className="ml-2 text-slate-800">{citation.document_title}: </strong>}
                                    <span>{citation.snippet}</span>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                <section className="border border-slate-200 bg-white rounded-lg p-5">
                    <div className="flex items-start gap-2 text-sm text-slate-600">
                        <Lock size={16} className="text-slate-500 shrink-0 mt-0.5" />
                        <div>
                            <div className="font-semibold text-slate-800">Privacy boundary</div>
                            <p className="mt-1">Uploaded deeds, photos, evidence files, case timeline entries, resident account details, and other private records are not available from this link.</p>
                        </div>
                    </div>
                </section>
            </div>
        </main>
    );
}
